import React, { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Popover } from 'react-tiny-popover'

import { EFolderStatus } from '../../../../domain/Folder/EFolderStatus'
import { ERole, roleBackOfficeList } from '../../../../domain/User/Erole'
import { EFolderPlatformStatus } from '../../../../domain/Folder/EFolderPlatformStatus'
import { PropsActions, ActionItem } from './types'
import storage from '../../../util/storage'
import { isFolderDuplicationEnabled } from '../../../util/envVariable'

import SignHistory from '../../svg/SignHistory'
import DownloadDoc from '../../svg/DownloadDoc'
import AbandonedFiles from '../../svg/AbandonedFiles'
import DuplicateFolder from '../../svg/DuplicateFolder'
import ForceValidateButton from '../../svg/ForceValidateButton'
import Edit from '../../svg/Edit'
import MoreVertical from '../../svg/MoreVertical'
import { EModule } from '../../../../domain/User/Emodule'

const FolderActions = ({
  FolderDetail,
  role,
  handleEdit,
  handleDownload,
  handleDocuSignHistory,
  handleManageDossier,
  toggle,
  toggleDuplicate,
  toggleForceValidate,
  allowDownloadDocument,
  setError,
  type,
  state
}: PropsActions): JSX.Element | null => {
  const { t } = useTranslation()
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false)
  const [isSingleHoverOpen, setIsSingleHoverOpen] = useState<boolean>(false)

  const actions: ActionItem[] = useMemo(() => {
    const list: ActionItem[] = []

    const statusHideAllButton: string[] = [EFolderStatus.Canceled, EFolderStatus.Rejected]
    const roleHideButtonDeleted: string[] = [ERole.Client, ERole.Prospect, ERole.Back_office_user, ERole.ManageDossier]
    const roleShowButtonDeletedBO: string[] = [ERole.ManageDossier]
    const roleHideButtonModify: string[] = [ERole.Back_office_user, ERole.ManageDossier]
    const roleHideButtonHistorySignature: string[] = [ERole.Client, ERole.Prospect]
    const roleHideManageDossier: string[] = [ERole.Client, ERole.Prospect, ERole.Back_office_user]
    const statusHideButtonDeleted: string[] = [EFolderStatus.Signed]
    const roleHideButtonDuplicate: string[] = [ERole.Client, ERole.Prospect, ERole.DirectProspect, ERole.Back_office_user, ERole.ManageDossier]
    const statusHideButtonDuplicate: string[] = [EFolderStatus.Signed]
    const roleShowForceValidate: string[] = [ERole.Back_office_user]
    const statusShowForceValidate: string[] = [EFolderStatus.Signed]
    const statusPlatformShowForceValidate: string[] = [EFolderPlatformStatus.Ongoing, EFolderPlatformStatus.Refused]
    const isGlobalHidden = statusHideAllButton.includes(FolderDetail.status)
    const userProfile = storage.getUserInformation()

    // --- 1. Download ---
    if (!isGlobalHidden && allowDownloadDocument && type !== 'manage' && type !== 'pre-signature-validation' &&
      (FolderDetail.status === 'signature' || FolderDetail.status === 'signed')) {
      list.push({
        id: 'download',
        label: t('button.download-document'),
        icon: <DownloadDoc />,
        onClick: handleDownload
      })
    }

    // --- 2. History ---
    if (!isGlobalHidden && !roleHideButtonHistorySignature.includes(role) && type !== 'manage' && type !== 'pre-signature-validation' &&
      (FolderDetail.status === EFolderStatus.Signature || FolderDetail.status === EFolderStatus.Signed)) {
      list.push({
        id: 'history',
        label: t('docusign.history'),
        icon: <SignHistory />,
        onClick: handleDocuSignHistory
      })
    }

    // --- 3. Duplicate ---
    if (!isGlobalHidden && !roleHideButtonDuplicate.includes(role) &&
      !statusHideButtonDuplicate.includes(FolderDetail.status) &&
      type !== 'manage' && isFolderDuplicationEnabled()) {
      list.push({
        id: 'duplicate',
        label: t('button.duplicate'),
        icon: <DuplicateFolder />,
        onClick: toggleDuplicate
      })
    }

    // --- 4. Edit / Modify / View ---
    let showEdit = false
    let editLabel = t('button.modify')

    const isStandardEdit = !roleHideButtonModify.includes(role) && type !== 'manage' && type !== 'pre-signature-validation' &&
      (FolderDetail.status !== EFolderStatus.Signature && FolderDetail.status !== EFolderStatus.Signed && FolderDetail.status !== EFolderStatus.Pre_signature_validation)
    const isCorrectionEdit = !roleHideButtonModify.includes(role) && type !== 'manage' && type !== 'pre-signature-validation' && state === 'correction' &&
      (FolderDetail.status === EFolderStatus.Signature || FolderDetail.status === EFolderStatus.Signed)
    const isBackOfficeView = roleHideButtonModify.includes(role) && type !== 'manage' && type !== 'pre-signature-validation'

    if (!isGlobalHidden) {
      if (isStandardEdit || isCorrectionEdit) {
        showEdit = true
      } else if (isBackOfficeView) {
        showEdit = true
        const isBOForm = FolderDetail.formId === storage.getBackOfficeFormId()
        editLabel = isBOForm && roleBackOfficeList.includes(role) ? t('button.modify') : t('button.view')
      } else if (FolderDetail.status === EFolderStatus.Pre_signature_validation && type !== 'manage' && type !== 'pre-signature-validation') {
        showEdit = true
        editLabel = t('button.view')
      }
    }

    if (showEdit) {
      list.push({
        id: 'edit',
        label: editLabel,
        icon: <Edit />,
        onClick: handleEdit
      })
    }

    let showAbandon = false
    if (!isGlobalHidden && type !== 'manage' && type !== 'pre-signature-validation') {
      const standardAbandon = !roleHideButtonDeleted.includes(role) && !statusHideButtonDeleted.includes(FolderDetail.status)
      const boAbandon = roleShowButtonDeletedBO.includes(role) && FolderDetail.formId === storage.getBackOfficeFormId() && !statusHideButtonDeleted.includes(FolderDetail.status)

      if (standardAbandon || boAbandon) {
        showAbandon = true
      }
    }

    if (showAbandon) {
      list.push({
        id: 'abandon',
        label: t('folder.abandon-folder'),
        icon: <AbandonedFiles color="var(--ink-6)" />,
        onClick: () => { setError(false); toggle(); },
        color: 'var(--ink-6)'
      })
    }

    // --- 6. Manage Dossier ---
    if (!isGlobalHidden && !roleHideManageDossier.includes(role) && type === 'manage') {
      list.push({
        id: 'manage',
        label: t('button.manage-dossier'),
        icon: <Edit />,
        onClick: handleManageDossier
      })
    }

    if (
      !isGlobalHidden
      && type === 'pre-signature-validation'
      && userProfile?.modules?.includes(EModule.PreSignatureValidator)
      && FolderDetail.status === EFolderStatus.Pre_signature_validation
    ) {
      list.push({
        id: 'manage',
        label: t('button.manage-dossier'),
        icon: <Edit />,
        onClick: handleManageDossier
      })
    }

    // --- 7. Duplicate (Special Case: Canceled) ---
    if (FolderDetail.status === EFolderStatus.Canceled && type !== 'manage' && isFolderDuplicationEnabled()) {
      list.push({
        id: 'duplicate-canceled',
        label: t('button.duplicate'),
        icon: <DuplicateFolder />,
        onClick: toggleDuplicate
      })
    }

    // --- 8. Force Validate ---
    if (roleShowForceValidate.includes(role) &&
      statusShowForceValidate.includes(FolderDetail.status) &&
      statusPlatformShowForceValidate.includes(FolderDetail.plateforme_state)) {
      list.push({
        id: 'force-validate',
        label: t('button.forceValidate'),
        icon: <ForceValidateButton />,
        onClick: toggleForceValidate
      })
    }    

    return list
  }, [
    FolderDetail, role, state, type, allowDownloadDocument, t,
    handleDownload, handleDocuSignHistory, toggleDuplicate, handleEdit,
    setError, toggle, handleManageDossier, toggleForceValidate
  ])

  if (actions.length === 0) return null

  if (actions.length === 1) {
    const action = actions[0]
    return (
      <div className='folder-actions'>
        <Popover
          isOpen={isSingleHoverOpen}
          positions={['bottom', 'top', 'left', 'right']}
          align={'center'}
          padding={10}
          reposition={true}
          onClickOutside={() => setIsMenuOpen(false)}
          content={<div className="box-helpers">{action.label}</div>}
        >
          <button
            data-id={FolderDetail.id}
            className={`button-reset button--medium button--width-fit ${action.id === 'abandon' ? 'button--files-state button--abandon' : 'button-icon-action'}`}
            onMouseEnter={() => setIsSingleHoverOpen(true)}
            onMouseLeave={() => setIsSingleHoverOpen(false)}
            onClick={action.onClick}
          >
            {action.icon}
          </button>
        </Popover>
      </div>
    )
  }

  return (
    <div className='folder-actions'>
      <Popover
        isOpen={isMenuOpen}
        positions={['bottom', 'left']}
        align={'end'}
        padding={4}
        onClickOutside={() => setIsMenuOpen(false)}
        content={
          <div className="dropdown-actions-menu">
            {actions.map((action) => (
              <button
                key={action.id}
                className="button-reset dropdown-actions-item"
                onClick={() => {
                  action.onClick()
                  setIsMenuOpen(false)
                }}
              >
                <span style={{ width: '20px', display: 'flex', justifyContent: 'center' }}>
                  {action.icon}
                </span>
                <span>{action.label}</span>
              </button>
            ))}
          </div>
        }
      >
        <button
          className="button-reset button--medium button--width-fit button-icon-action"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          style={{ padding: '5px' }}
        >
          <MoreVertical />
        </button>
      </Popover>
    </div>
  )
}

export default FolderActions